import { useMemo } from "react";
import { useCalculation, useCalculationField } from "@ploy-lib/calculation";
import { useDispatch } from "@ploy-lib/calculation";
import { useFetcher, useResource } from "@rest-hooks/core";
import { useOptionSourceFields } from "./utils";
import { getIn, useField, useFormikContext } from "formik";
import { tableSchemas } from "./tableSchema";
const identityArrayWithDefault = (x) => x || [];
const identity = (x) => x;
export const CalcRulesDataProvider = (props) => {
    const { tableType, tableColumns = [], namespace, name, tableColor, children, convertRawStorageToStorageDataRows = identityArrayWithDefault, convertStorageDataToRowCells = identity, convertRowCellsToStorageData = identity, convertStorageDataRowsToRawStorage = identity, disabled } = props;
    const { values: calcValues } = useCalculation("values");
    const { values } = useFormikContext();
    const actualTableColor = tableColor
        ? getIn(calcValues, tableColor) ?? getIn(values, tableColor) ?? tableColor
        : undefined;
    const [field, , { setValue }] = useField(name);
    const { value: originalValues = field.value, enabled: fieldEnabled } = useCalculationField({
        namespace,
        name: tableType
    });
    const columnsWithOptionSource = tableColumns.filter(col => col.optionSource !== undefined);
    const optionSources = useOptionSourceFields(columnsWithOptionSource);
    const columnsWithOptionValues = tableColumns.map(col => {
        const optionValueCandidate = optionSources[`${col.tableType}.${col.name}`];
        col.optionValues =
            col.optionSource &&
                Array.isArray(optionValueCandidate) &&
                optionValueCandidate.length > 0
                ? optionValueCandidate
                : col.optionValues || [];
        return col;
    });
    const schema = tableSchemas[props.tableSchema || ""];
    const filterDataRowsFn = schema?.tableMetadata.rowFilterer;
    const sortDataRowsFn = schema?.tableMetadata.rowSorter;
    const dispatch = useDispatch();
    const relevantDataRows = useMemo(() => convertRawStorageToStorageDataRows(originalValues), [convertRawStorageToStorageDataRows, originalValues]);
    const relevantCellRows = useMemo(() => {
        let data = relevantDataRows.map(convertStorageDataToRowCells);
        if (filterDataRowsFn)
            data = filterDataRowsFn(data, calcValues);
        if (sortDataRowsFn)
            data.sort(sortDataRowsFn);
        return data;
    }, [
        convertStorageDataToRowCells,
        relevantDataRows,
        filterDataRowsFn,
        sortDataRowsFn,
        calcValues
    ]);
    return children({
        rows: relevantCellRows,
        tableColumns: columnsWithOptionValues,
        tableColor: actualTableColor,
        disabled: disabled || !fieldEnabled,
        saveElement: (values, elementIndex) => {
            const convertedValues = convertStorageDataRowsToRawStorage(values.map(convertRowCellsToStorageData), originalValues);
            dispatch({
                type: "patch",
                payload: {
                    patches: [
                        {
                            target: tableType,
                            namespace,
                            value: convertedValues,
                            overwrite: true,
                            changeTrigger: `${tableType}_manualSave`
                        }
                    ]
                }
            });
            if (field) {
                setValue(convertedValues);
            }
        },
        deleteElement: (values, elementIndex) => {
            const convertedValues = convertStorageDataRowsToRawStorage(values
                .filter((v, idx) => idx !== elementIndex)
                .map(convertRowCellsToStorageData), originalValues);
            dispatch({
                type: "patch",
                payload: {
                    patches: [
                        {
                            target: tableType,
                            namespace,
                            value: convertedValues,
                            overwrite: true,
                            changeTrigger: `${tableType}_manualSave`
                        }
                    ]
                }
            });
            if (field) {
                setValue(convertedValues);
            }
        }
    });
};
export const ResourceDataProvider = (props) => {
    const { resource, resourceId, tableColumns, children, tableColor, disabled } = props;
    const data = useResource(resource.list(), {});
    const create = useFetcher(resource.create());
    const update = useFetcher(resource.update());
    const del = useFetcher(resource.delete());
    const isNewElement = (value) => {
        // Assume new element if it does not have a key
        return !!value.pk();
    };
    const onRowSave = (entity) => {
        if (isNewElement(entity)) {
            create({}, entity, [
                [resource.list(), {}, (newId, oldIds) => [...oldIds, newId]]
            ]);
        }
        else {
            update({ [resourceId]: entity[resourceId] }, entity);
        }
    };
    const onRowDelete = (entity) => {
        del({ [resourceId]: entity[resourceId] });
    };
    const actualTableColor = tableColor
        ? getIn(data, tableColor) ?? tableColor
        : undefined;
    const schema = tableSchemas[props.tableSchema || ""];
    const tableRowFilter = schema?.tableMetadata.rowFilterer;
    const tableRowSorter = schema?.tableMetadata.rowSorter;
    const relevantCellRows = useMemo(() => {
        let rows = Array.isArray(data)
            ? data
            : Array.isArray(data.records)
                ? data.records
                : [];
        if (tableRowFilter)
            rows = tableRowFilter(rows);
        if (tableRowSorter)
            rows.sort(tableRowSorter);
        return rows;
    }, [data, tableRowFilter, tableRowSorter]);
    return children({
        rows: relevantCellRows,
        tableColumns: tableColumns,
        tableColor: actualTableColor,
        saveElement: (values, index) => {
            index !== undefined && onRowSave(values[index]);
        },
        deleteElement: (values, index) => index && onRowDelete(values[index]),
        disabled: disabled
    });
};
//# sourceMappingURL=TableDataProvider.js.map